import {
  validatePhoneNumber,
  validateZipCode,
  validateFirstNameAndLast,
  validateAddress,
  validateEmail,
} from "../Helper/Helper";
import {
  Errors,
  Field,
  FormValues,
  Element,
} from "../../../Interface/scheduler/form";
import {
  AppConstants,
  EMAIL_VALIDATION_ERROR,
  PRICING_ID,
  PROTYPE_COMMERCIAL_TEXT,
  RegexConstants,
  ZIP_CODE_VALIDATION_ERROR,
} from "../../../constants/constant";
import { sessionStorageUtils } from "../../../utility/utility";
import {
  PricingConfig,
  Field as Service,
} from "../../../feature/servicePricingTabs/components/ServiceTabDetails.interface";

export const getValidationError = (
  field: Field,
  value: string | boolean | string[]
): string | null => {
  if (field.required === true) {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      const defaultError = `${field.label} is required`;
      return field.errorMessage || defaultError;
    }
  }

  if (!value) return null;

  switch (field.type) {
    case "tel":
      if (!validatePhoneNumber(value as string)) {
        return `${field.label} must be a 10-digit phone number`;
      }
      break;
    case "email":
      if (!validateEmail(value as string)) {
        return `${field.label} ${EMAIL_VALIDATION_ERROR}`;
      }
      break;
    case "zipcode":
      if (!validateZipCode(value as string)) {
        return `Invalid ${field.label || ZIP_CODE_VALIDATION_ERROR} format.`;
      }
      break;
    case "name":
      if (!validateFirstNameAndLast(value as string)) {
        return `Invalid ${field.label} format`;
      }
      break;
    case "address":
      if (!validateAddress(value as string)) {
        return `Invalid ${field.label} format`;
      }
      break;
    default:
      if (field.id === "zipcode" && !validateZipCode(value as string)) {
        return `Invalid ${field.label || ZIP_CODE_VALIDATION_ERROR} format.`;
      }
      if (field.id === "address" && !validateAddress(value as string)) {
        return `Invalid ${field.label} format`;
      }
      if (
        field.id === "city" &&
        !RegexConstants?.CITY_NAME?.test(value as string)
      ) {
        return `Invalid ${field.label} format`;
      }
      break;
  }

  return null;
};

export const validateFields = (
  fields: Field[],
  formValues: FormValues,
  setErrors: React.Dispatch<React.SetStateAction<Errors>>,
  activeTabId?: string,
  errors?: Errors
): boolean => {
  const newErrors: Errors = {};
  fields.forEach((field) => {
    if (field.required && field.type === "file-upload") {
      const files = sessionStorage.getItem("fileUrls");
      const checkFiles = JSON.parse(files || "{}");
      if (Object.keys(checkFiles).length === 0) {
        newErrors[field.id] =
          field.errorMessage || `${field.label} is required`;
      }
    } else if (
      field.id === AppConstants.FIELD_ID_PRICE_QUOTE_SELECTION &&
      (!formValues?.priceQuoteEstimateAvailable ||
        formValues?.existingcustomer === "true" ||
        formValues?.proptype === PROTYPE_COMMERCIAL_TEXT)
    ) {
      return true;
    } else if (field.type === "tabs") {
      field.tabs?.forEach((tab) => {
        if (!activeTabId || tab.id === activeTabId) {
          tab.fields?.forEach((tabField) => {
            if (tabField.required && !formValues[tabField.id]) {
              newErrors[tabField.id] = tabField.errorMessage;
            }
          });
        }
      });
    } else if (field.type === "email") {
      const emailSenGridError = sessionStorageUtils.getItem(
        "emailValidationError"
      );
      if (!emailSenGridError) {
        const error = getValidationError(field, formValues[field.id]);
        if (errors?.[field.id] || error) {
          newErrors[field.id] = error || errors?.[field.id];
        }
      }
    } else {
      const value = formValues[field.id];
      const error = getValidationError(field, value as string);
      if (error) {
        newErrors[field.id] = error;
      }
      field.options?.forEach((option) => {
        const selectedValue = formValues[field.id];
        if (selectedValue === option.id) {
          option.child?.forEach((child) => {
            if (child) {
              const value = formValues[child.id];
              const error = getValidationError(child, value as string);
              if (error) {
                newErrors[child.id] = error;
              }
              // Validate nested children
              child.options?.forEach((nestedOption) => {
                const nestedSelectedValue = formValues[child.id];
                if (nestedSelectedValue === nestedOption.id) {
                  nestedOption.child?.forEach((nestedChild) => {
                    if (nestedChild) {
                      const nestedValue = formValues[nestedChild.id];
                      const nestedError = getValidationError(
                        nestedChild,
                        nestedValue as string
                      );
                      if (nestedError) {
                        newErrors[nestedChild.id] = nestedError;
                      }
                    }
                  });
                }
              });
            }
          });
        }
      });
    }
  });
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

export const getPricingValidationError = (
  field: PricingConfig | Service | Field,
  value: string | boolean | string[]
): string | null => {
  if (field.required === true && !value) {
    const defaultError = `${field.id} is required`;
    return field.errorMessage || defaultError;
  }
  return null;
};

export const validatePricingFields = (
  stepType: string,
  data: PricingConfig,
  formValues: FormValues,
  setErrors: React.Dispatch<React.SetStateAction<Errors>>
) => {
  if (stepType === AppConstants.HOW_WE_CAN_REACH && data?.id === PRICING_ID) {
    const newErrors: Errors = {};
    if (data?.required && !formValues[data.id]) {
      newErrors[data.id] = data.errorMessage;
    }
    const selectedService = data.tabs.find(
      (item) => item.id === formValues[data.id]
    );
    const element = selectedService?.elements.find(
      (element) => element.type === "form"
    );
    element?.fields.forEach((field) => {
      if (field.type === "checkbox") {
        if (formValues.pricingError) return true;
        else if (field.required && formValues[field.id] !== "true")
          newErrors[field.id] = field.errorMessage;
      } else if (field.required && !formValues[field.id]) {
        newErrors[field.id] = field.errorMessage;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }
  return true;
};

export const validateServiceSelectionFields = (
  element: Element,
  formValues: FormValues,
  setErrors: React.Dispatch<React.SetStateAction<Errors>>
) => {
  const newErrors: Errors = {};
  if (element.required) {
    if (!formValues[element.id] || formValues[element.id]?.length === 0) {
      newErrors[element.id] =
        element.errorMessage || `${element.label} is required`;
    }
  }
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

export const validateMapAreaSelectionFields = (
  element: Element,
  formValues: FormValues,
  setErrors: React.Dispatch<React.SetStateAction<Errors>>
) => {
  const newErrors: Errors = {};

  if (formValues[element.id] === "0") {
    newErrors[element.id] =
      element.errorMessage || `Please select at least one area on the map`;
  }
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};
